import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { ReactElement, useState } from 'react';
import { Copy } from 'react-feather';
import { toast } from 'react-toastify';
import toastOptions from '../../helpers/toastOptions';

type Cell = string | number | null | undefined;

type CopyTsvButtonProps = {
  header: string[];
  // Either the rows to copy, or a loader that fetches them when clicked (for
  // a paged table whose whole result set should be copied).
  rows?: Cell[][];
  getRows?: () => Promise<Cell[][]>;
  label: string;
  disabled?: boolean;
};

const text = (value: Cell): string => (value === null || value === undefined ? '' : String(value).replace(/[\t\r\n]+/g, ' '));
const escapeHtml = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Copies a table so that a paste into Excel or Google Sheets lands one value
// per cell: tab-separated text for plain-text targets, plus an HTML table for
// spreadsheets that prefer it. Falls back to text only where the richer
// clipboard API is unavailable.
export const copyTable = async (header: string[], rows: Cell[][]): Promise<void> => {
  const lines = [header, ...rows];
  const tsv = lines.map((row) => row.map(text).join('\t')).join('\n');
  const html =
    '<table>' +
    lines
      .map((row, i) => '<tr>' + row.map((c) => (i === 0 ? '<th>' : '<td>') + escapeHtml(text(c)) + (i === 0 ? '</th>' : '</td>')).join('') + '</tr>')
      .join('') +
    '</table>';
  const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  if (clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      await clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([tsv], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ]);
      return;
    } catch {
      /* fall through to plain text */
    }
  }
  if (clipboard?.writeText) {
    await clipboard.writeText(tsv);
    return;
  }
  throw new Error('clipboard unavailable');
};

export const CopyTsvButton = ({ header, rows, getRows, label, disabled }: CopyTsvButtonProps): ReactElement => {
  const [busy, setBusy] = useState(false);
  const empty = !getRows && !(rows && rows.length);

  const onClick = async () => {
    setBusy(true);
    try {
      const data = getRows ? await getRows() : rows ?? [];
      if (!data.length) {
        toast.info('Nothing to copy', toastOptions);
        return;
      }
      await copyTable(header, data);
      toast.success(`${label} copied (${data.length} rows)`, toastOptions);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`, toastOptions);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tooltip arrow placement="top" title="Copy table for Excel">
      <span>
        <IconButton size="small" aria-label={`Copy ${label}`} disabled={disabled || busy || empty} onClick={onClick}>
          {busy ? <CircularProgress size={14} /> : <Copy size={14} />}
        </IconButton>
      </span>
    </Tooltip>
  );
};
